import React, { useState } from 'react';
import axios from 'axios';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { FaFileAlt, FaSpinner, FaDownload, FaExclamationCircle, FaEye } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const documentTypes = [
  { type: 'contrato', label: 'Contrato', placeholder: 'Detalles del contrato...' },
  { type: 'acuerdo', label: 'Acuerdo', placeholder: 'Detalles del acuerdo...' },
  { type: 'propuesta', label: 'Propuesta', placeholder: 'Detalles de la propuesta...' },
  { type: 'memorandum', label: 'Memorándum', placeholder: 'Detalles del memorándum...' },
  { type: 'reporte', label: 'Reporte', placeholder: 'Detalles del reporte...' },
  { type: 'factura', label: 'Factura', placeholder: 'Detalles de la factura...' },
  { type: 'solicitud', label: 'Solicitud', placeholder: 'Detalles de la solicitud...' },
  { type: 'política', label: 'Política', placeholder: 'Detalles de la política...' },
  { type: 'carta de recomendación', label: 'Carta de Recomendación', placeholder: 'Detalles de la carta...' },
  { type: 'acta', label: 'Acta', placeholder: 'Detalles del acta...' },
];

const DocumentCreator = () => {
  const [documentType, setDocumentType] = useState('');
  const [details, setDetails] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateDocument = async () => {
    if (!documentType || !details) {
      toast.error('Por favor, completa todos los campos');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post('https://api.openai.com/v1/chat/completions', {
        model: "gpt-3.5-turbo",
        messages: [{
          role: "system",
          content: "You are a helpful assistant that generates legal documents."
        }, {
          role: "user",
          content: `Generate a ${documentType} with the following details: ${details}`
        }],
      }, {
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
          'Content-Type': 'application/json'
        }
      });
      setGeneratedContent(response.data.choices[0].message.content);
      toast.success('Documento generado con éxito');
    } catch (error) {
      console.error('Error generating document:', error);
      setError('Error al generar el documento. Por favor, intenta de nuevo.');
      toast.error('Error al generar el documento');
    } finally {
      setIsLoading(false);
    }
  };

  const createPDF = async () => {
    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage();
    const { width, height } = page.getSize();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

    // Dibujar un ícono como texto (emoji de documento)
    page.drawText("📄", { // Emoji de documento
      x: 50,
      y: height - 100,
      size: 50,
      font: font,
      color: rgb(0.1, 0.1, 0.1),
    });

    page.drawText(documentType, {
      x: 50,
      y: height - 150,
      size: 24,
      font: font,
      color: rgb(0.1, 0.1, 0.1),
    });

    page.drawText(generatedContent, {
      x: 50,
      y: height - 200,
      size: 12,
      font: font,
      color: rgb(0.1, 0.1, 0.1),
      maxWidth: width - 100,
    });

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${documentType}_${new Date().toISOString()}.pdf`;
    link.click();
    toast.info('PDF descargado');
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg max-w-2xl mx-auto">
      <ToastContainer />
      <h2 className="text-3xl font-bold mb-6 text-gray-800 flex items-center">
        <FaFileAlt className="mr-2 text-blue-500" />
        Creador de Documentos
      </h2>
      <select 
        className="w-full p-3 mb-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        value={documentType} 
        onChange={(e) => {
          setDocumentType(e.target.value);
          setDetails('');
        }}
      >
        <option value="">Selecciona el tipo de documento</option>
        {documentTypes.map((doc) => (
          <option key={doc.type} value={doc.type}>
            {doc.label}
          </option>
        ))}
      </select>
      <textarea
        className="w-full p-3 mb-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        placeholder={
          documentTypes.find((doc) => doc.type === documentType)?.placeholder || 'Ingresa los detalles del documento'
        }
        rows="6"
      />
      <button 
        className="bg-blue-500 text-white p-3 rounded-lg mr-2 hover:bg-blue-600 transition duration-300 flex items-center justify-center w-full mb-4"
        onClick={generateDocument}
        disabled={isLoading}
      >
        {isLoading ? <FaSpinner className="animate-spin mr-2" /> : <FaFileAlt className="mr-2" />}
        {isLoading ? 'Generando...' : 'Generar Documento'}
      </button>
      {generatedContent && (
        <>
          <button 
            className="bg-green-500 text-white p-3 rounded-lg hover:bg-green-600 transition duration-300 flex items-center justify-center w-full mb-4"
            onClick={createPDF}
          >
            <FaDownload className="mr-2" /> Descargar PDF
          </button>
          <button 
            className="bg-yellow-500 text-white p-3 rounded-lg hover:bg-yellow-600 transition duration-300 flex items-center justify-center w-full mb-4"
            onClick={() => window.open(URL.createObjectURL(new Blob([generatedContent], { type: 'text/plain' })), '_blank')}
          >
            <FaEye className="mr-2" /> Previsualizar Documento
          </button>
        </>
      )}
      {error && (
        <div className="text-red-500 mb-4 flex items-center">
          <FaExclamationCircle className="mr-2" /> {error}
        </div>
      )}
      {generatedContent && (
        <div className="mt-6 p-4 border rounded-lg bg-gray-50">
          <h3 className="font-bold mb-2 text-lg">Documento Generado:</h3>
          <p className="whitespace-pre-wrap">{generatedContent}</p>
        </div>
      )}
    </div>
  );
};

export default DocumentCreator;
